import { createBrowserRouter, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { SUPPORTED_HOMEPAGE_LOCALES } from '../i18n/homepage';
import { lazy, Suspense, type ReactNode } from 'react';
import { RouteErrorBoundary } from '../components/errors/RouteErrorBoundary';
import { AuthGuard } from './guards/AuthGuard';
import { EmailVerificationGuard } from './guards/EmailVerificationGuard';
import { AdminGuard } from './guards/AdminGuard';
import { BanGate } from '../components/guards/BanGate';
import { blogRoutes } from './blog-routes';
import { LpPage } from '../pages/landing/LpPage';
import { LP_SLUGS } from '../data/landingPages';

// Layouts
import { AppLayout } from '../layouts/AppLayout';
import { AuthLayout } from '../layouts/AuthLayout';

// Pages — static imports (no invite-renderer/builder deps)
import { LandingPage } from '../pages/landing/LandingPage';
import { RsvpFeaturePage } from '../pages/features/RsvpFeaturePage';
import { TablePlanFeaturePage } from '../pages/features/TablePlanFeaturePage';
import { PrivacyPage } from '../pages/legal/PrivacyPage';
import { TermsPage } from '../pages/legal/TermsPage';
import { LoginPage } from '../pages/auth/LoginPage';
import { RegisterPage } from '../pages/auth/RegisterPage';
import { ForgotPasswordPage } from '../pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/auth/ResetPasswordPage';
import { OAuthCallbackPage } from '../pages/auth/OAuthCallbackPage';
import { VerifyEmailPage } from '../pages/auth/VerifyEmailPage';
import { ProfilePage } from '../pages/profile/ProfilePage';
import { SettingsPage } from '../pages/settings/SettingsPage';
import { BillingLayout } from '../pages/billing/BillingLayout';
import { BrowsePage } from '../pages/browse/BrowsePage';
import { AcceptInvitePage } from '../pages/team/AcceptInvitePage';
import { GuestListPage } from '../pages/guests/GuestListPage';
import { AllergyDataPage } from '../pages/guests/AllergyDataPage';
import { ContactPage } from '../pages/contact/ContactPage';
import { PublicContactPage } from '../pages/contact/PublicContactPage';
import { FontPairingPage } from '../pages/test/FontPairingPage';
import ErrorTest from '../pages/test/ErrorTest';
import { UnsubscribedPage } from '../pages/unsubscribe/UnsubscribedPage';

// Pages — lazy (these import invite-renderer/builder or admin — keep out of main chunk
// so the invite/admin chunks are never statically linked to main, preventing the
// react→invite→framer-motion→react circular chunk init dependency).
const AdminPage = lazy(() => import('../pages/admin/AdminPage').then(m => ({ default: m.AdminPage })));
const ComponentLibraryPage = lazy(() => import('../pages/components/ComponentLibraryPage').then(m => ({ default: m.ComponentLibraryPage })));
const HomePage = lazy(() => import('../pages/home/HomePage').then(m => ({ default: m.HomePage })));
const InvitesListPage = lazy(() => import('../pages/invites/InvitesListPage').then(m => ({ default: m.InvitesListPage })));
const InviteBuilderPage = lazy(() => import('../pages/invites/InviteBuilderPage').then(m => ({ default: m.InviteBuilderPage })));
const InvitePreviewPage = lazy(() => import('../pages/invites/InvitePreviewPage').then(m => ({ default: m.InvitePreviewPage })));
const GuestViewPage = lazy(() => import('../pages/invites/GuestViewPage').then(m => ({ default: m.GuestViewPage })));
const TestBuilderPage = lazy(() => import('../pages/invites/TestBuilderPage').then(m => ({ default: m.TestBuilderPage })));
const TestPreviewPage = lazy(() => import('../pages/invites/TestPreviewPage').then(m => ({ default: m.TestPreviewPage })));
const TablePlanPage = lazy(() => import('../pages/table-plan/TablePlanPage'));
const PurchasePage = lazy(() => import('../pages/purchase/PurchasePage').then(m => ({ default: m.PurchasePage })));
const SuspendedScreen = lazy(() => import('../pages/SuspendedScreen').then(m => ({ default: m.SuspendedScreen })));
const WaxSealDemo = lazy(() => import('../pages/test/WaxSealDemo').then(m => ({ default: m.WaxSealDemo })));
const InviteEmailsPage = lazy(() => import('../pages/invites/InviteEmailsPage').then(m => ({ default: m.InviteEmailsPage })));

function L({ children }: { children: ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

function SocialRedirect({ source, medium = 'social' }: { source: string; medium?: string }) {
  const [searchParams] = useSearchParams();
  const params: Record<string, string> = { utm_source: source, utm_medium: medium };
  for (const key of ['utm_campaign', 'utm_content', 'utm_term'] as const) {
    const val = searchParams.get(key);
    if (val) params[key] = val;
  }
  localStorage.setItem('utm_params', JSON.stringify({ params, ts: Date.now() }));
  return <Navigate to="/" replace />;
}

function InviteGuestsRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/guests?invite=${id}`} replace />;
}

export const router = createBrowserRouter([
  // Landing page at root (public)
  {
    path: '/',
    element: <LandingPage />,
  },

  // Localised landing pages
  ...SUPPORTED_HOMEPAGE_LOCALES.map(locale => ({
    path: `/${locale}`,
    element: <LandingPage locale={locale} />,
  })),

  // SEO landing pages — generated from LP_SLUGS data file
  ...LP_SLUGS.map(slug => ({ path: `/${slug}`, element: <LpPage /> })),

  // Feature pages
  { path: '/features/rsvp', element: <RsvpFeaturePage /> },
  { path: '/wedding-seating-planner', element: <TablePlanFeaturePage /> },

  // Vanity social source routes — capture UTM then redirect to landing
  { path: '/instagram', element: <SocialRedirect source="instagram" /> },
  { path: '/tiktok', element: <SocialRedirect source="tiktok" /> },
  { path: '/google', element: <SocialRedirect source="google" medium="search" /> },

  // Blog routes (SSR on server, client-side navigation after hydration)
  ...blogRoutes,

  // Public contact/support page
  {
    path: '/contact',
    element: <PublicContactPage />,
  },

  // Legal pages (public)
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },
  {
    path: '/terms',
    element: <TermsPage />,
  },

  // Public auth routes (no /app prefix)
  {
    path: '/login',
    element: <AuthLayout />,
    children: [
      { index: true, element: <LoginPage /> },
    ],
  },
  {
    path: '/register',
    element: <AuthLayout />,
    children: [
      { index: true, element: <RegisterPage /> },
    ],
  },
  {
    path: '/forgot-password',
    element: <AuthLayout />,
    children: [
      { index: true, element: <ForgotPasswordPage /> },
    ],
  },
  {
    path: '/reset-password',
    element: <AuthLayout />,
    children: [
      { index: true, element: <ResetPasswordPage /> },
    ],
  },

  // OAuth callback (standalone, no layout)
  {
    path: '/oauth/callback',
    element: <OAuthCallbackPage />,
  },

  // Component library (public, for design audits)
  {
    path: '/components',
    element: <L><ComponentLibraryPage /></L>,
  },

  // Marketing unsubscribe confirmation
  {
    path: '/unsubscribed',
    element: <UnsubscribedPage />,
  },

  // Trial expired
  {
    path: '/trial-expired',
    element: <L><SuspendedScreen /></L>,
  },
  // Legacy redirect
  {
    path: '/suspended',
    element: <Navigate to="/trial-expired" replace />,
  },

  // Standalone purchase page for suspended/trial-expired users
  {
    path: '/purchase',
    element: <L><PurchasePage /></L>,
  },

  // Team invite acceptance (standalone, handles own auth state)
  {
    path: '/invites/:token',
    element: <AcceptInvitePage />,
  },

  // Guest-specific view (slug + code, no auth required)
  {
    path: '/i/:slug/:code',
    element: <L><GuestViewPage /></L>,
    errorElement: <RouteErrorBoundary />,
  },

  // Public invite view (slug only, no auth required)
  {
    path: '/i/:slug',
    element: <L><GuestViewPage /></L>,
    errorElement: <RouteErrorBoundary />,
  },

  // Public builder (no auth required to use, auth required to save)
  {
    path: '/create',
    element: <BanGate><L><TestBuilderPage /></L></BanGate>,
    errorElement: <RouteErrorBoundary />,
  },

  // Public preview - shows preview from localStorage data
  {
    path: '/preview',
    element: <L><TestPreviewPage /></L>,
    errorElement: <RouteErrorBoundary />,
  },

  // Legacy test route
  {
    path: '/test/builder',
    element: <L><TestBuilderPage /></L>,
  },
  {
    path: '/test/wax-seal',
    element: <L><WaxSealDemo /></L>,
  },
  {
    path: '/test/fonts',
    element: <FontPairingPage />,
  },
  {
    path: '/test/error',
    element: <ErrorTest />,
    errorElement: <RouteErrorBoundary />,
  },

  // Email verification (requires auth, standalone page)
  {
    path: '/verify-email',
    element: (
      <AuthGuard>
        <VerifyEmailPage />
      </AuthGuard>
    ),
  },

  // Invite preview (requires auth, standalone fullscreen)
  {
    path: '/app/invites/:id/preview',
    element: (
      <AuthGuard>
        <L><InvitePreviewPage /></L>
      </AuthGuard>
    ),
  },

  // Main app routes under /app (requires auth + verified email)
  {
    path: '/app',
    element: (
      <AuthGuard>
        <EmailVerificationGuard>
          <AppLayout />
        </EmailVerificationGuard>
      </AuthGuard>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/app/home" replace /> },
      { path: 'home', element: <L><HomePage /></L> },
      { path: 'invites', element: <L><InvitesListPage /></L> },
      { path: 'invites/:id/edit', element: <L><InviteBuilderPage /></L> },
      { path: 'invites/:id/guests', element: <InviteGuestsRedirect /> },
      { path: 'invites/:id/emails', element: <L><InviteEmailsPage /></L> },
      { path: 'guests', element: <GuestListPage /> },
      { path: 'guests/allergies', element: <L><AllergyDataPage /></L> },
      { path: 'table-plan', element: <L><TablePlanPage /></L> },
      { path: 'rsvp', element: <Navigate to="/app/guests" replace /> },
      { path: 'browse', element: <BrowsePage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'billing', element: <BillingLayout /> },
      { path: 'plans', element: <Navigate to="/app/billing?tab=plans" replace /> },
      { path: 'team', element: <Navigate to="/app/settings?tab=collaborators" replace /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'admin', element: <Navigate to="/app/admin/dashboard" replace /> },
      { path: 'admin/:tab', element: <AdminGuard><L><AdminPage /></L></AdminGuard> },
      { path: 'admin/blog/:postId', element: <AdminGuard><L><AdminPage /></L></AdminGuard> },
      { path: 'admin/social/:carouselId', element: <AdminGuard><L><AdminPage /></L></AdminGuard> },
      { path: 'components', element: <L><ComponentLibraryPage /></L> },
    ],
  },

  // Catch-all redirect to landing
  { path: '*', element: <Navigate to="/" replace /> },
]);
